import { InputComponents } from 'types/auth';

// Shared between ImportTransactionsDialog (which consumes an uploaded file)
// and DownloadTemplateSection (which produces the file to fill in).
//
// displayLabel in particular MUST stay shared: the template's header text is
// produced by it, and the import's auto-map-by-name matches an uploaded
// header against it. If the two ever drift, auto-mapping a freshly downloaded
// template silently stops matching anything.

/** Field types that need an in-app capture step (camera, e-sign, file upload,
 * nested sub-forms) and simply can't be represented by a spreadsheet cell. */
export const NON_IMPORTABLE_TYPES: InputComponents[] = [
  InputComponents.CAMERA,
  InputComponents.FILE,
  InputComponents.ESIGN,
  InputComponents.FORM
];

/** Field types whose raw text needs to be translated into a specific workflow
 * value (a dropdown option, a resource record, a boolean) rather than passed
 * through as-is. */
export const VALUE_MAPPABLE_TYPES: InputComponents[] = [
  InputComponents.SELECT,
  InputComponents.RADIO,
  InputComponents.CHECKBOX,
  InputComponents.SWITCH,
  InputComponents.RESOURCE
];

export const IGNORE_VALUE = '__ignore__';

export type DocType = 'excel' | 'csv';

export const normalizeHeader = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const splitMulti = (raw: any): string[] => {
  if (raw === undefined || raw === null || raw === '') return [];
  return String(raw)
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const isTruthy = (raw: any) =>
  ['true', '1', 'yes', 'y', 'on'].includes(
    String(raw ?? '')
      .trim()
      .toLowerCase()
  );

export const stepLabel = (step: any) => step?.title || step?.stepType || 'Step';

/** A step can be driven from a file as long as it has at least one field. A
 * step with NO fields at all — a pure "instructions" step with nothing to
 * fill in — is skipped rather than stopping the automation chain. */
export const reachableStepsOf = (steps: any[]) => steps.filter((step) => (step?.component?.fields || []).length > 0);

/** Flat list of every importable field across every reachable step, each
 * tagged with the step it belongs to (for grouping the mapping UI and for
 * building the import payload). */
export const importableFieldsOf = (reachableSteps: any[]) =>
  reachableSteps.flatMap((step) =>
    (step.component?.fields || [])
      .filter((f: any) => !NON_IMPORTABLE_TYPES.includes(f.inputComponent))
      .map((f: any) => ({ ...f, __step: step }))
  );

/** Disambiguate identically-named fields that live on different steps (e.g.
 * two steps both have a "Notes" field) so template headers and the mapping UI
 * are unambiguous. Returns a labeller closed over the duplicate counts. */
export const makeDisplayLabel = (importableFields: any[]) => {
  const counts: Record<string, number> = {};
  importableFields.forEach((f) => {
    const key = normalizeHeader(f.question);
    counts[key] = (counts[key] || 0) + 1;
  });
  return (field: any) => {
    const dupe = (counts[normalizeHeader(field.question)] || 0) > 1;
    return dupe ? `${field.question} (${stepLabel(field.__step)})` : field.question;
  };
};

// ---------- Saved-mapping wire format ----------
//
// The server stores the mappings as an ARRAY while the component works in a
// Record, and the conversion lives here so that flip only exists in one place.
//
// Only the column -> field mapping is saved. Deliberately NOT saved:
//   - value mappings (a file value -> a workflow option/resource), and
//   - the per-row values typed in for a required field no column fills.
// Both are tied to the contents of one specific uploaded file rather than to
// its shape, so neither carries meaning into the next import.

export interface WireFieldMapping {
  fieldUid: string;
  stepUid?: string | null;
  question?: string | null;
  column: string;
}

export interface WireImportMapping {
  columns: string[];
  fieldMappings: WireFieldMapping[];
}

/** Component state -> request body. Only fields that actually have a column
 * are saved; "Do not import" is the absence of an entry, not a stored ''. */
export const toWireMapping = (columns: string[], fieldMapping: Record<string, string>, importableFields: any[]): WireImportMapping => {
  const fieldByUid = new Map<string, any>(importableFields.map((f) => [f.uid, f]));

  const fieldMappings: WireFieldMapping[] = Object.entries(fieldMapping)
    .filter(([, column]) => !!column)
    .map(([fieldUid, column]) => {
      const field = fieldByUid.get(fieldUid);
      return {
        fieldUid,
        stepUid: field?.__step?.uid ?? null,
        question: field?.question ?? null,
        column
      };
    });

  return { columns, fieldMappings };
};

/** Response body -> component state. */
export const fromWireMapping = (doc: any): { columns: string[]; fieldMapping: Record<string, string> } => {
  const fieldMapping: Record<string, string> = {};
  (Array.isArray(doc?.fieldMappings) ? doc.fieldMappings : []).forEach((entry: any) => {
    if (entry?.fieldUid && entry?.column) fieldMapping[entry.fieldUid] = entry.column;
  });

  return {
    columns: Array.isArray(doc?.columns) ? doc.columns : [],
    fieldMapping
  };
};
